import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import { lumi } from '@/lib/lumi';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import {ArrowLeft, Briefcase, MapPin, Clock, FileText, CheckCircle2} from 'lucide-react';

const CARGOS = [
  'barman', 'garcom', 'churrasqueiro', 'cozinheiro', 
  'passador', 'sommelier', 'auxiliar_cozinha', 'lava_pratos', 
  'recepcionista', 'bailarino', 'capo_sala'
];

function CriarVaga() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [titulo, setTitulo] = useState('');
  const [cargo, setCargo] = useState('garcom');
  const [regimeTrabalho, setRegimeTrabalho] = useState('full_time');
  const [tipoContrato, setTipoContrato] = useState('fixa');
  const [valorHora, setValorHora] = useState('');
  const [horasSemanais, setHorasSemanais] = useState('');
  const [localizacao, setLocalizacao] = useState('');
  const [descricao, setDescricao] = useState('');
  const [infoContrato, setInfoContrato] = useState('');
  
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isAuthenticated) navigate('/cadastro');
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (regimeTrabalho === 'full_time') {
      setHorasSemanais('40');
    } else if (regimeTrabalho === 'part_time') {
      setHorasSemanais('20');
    }
  }, [regimeTrabalho]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading || !user?.userId) return;
    setError('');
    if (!titulo.trim() || !localizacao.trim() || !cargo) return setError('Preencha título, cargo e localização.');
    if (!Number.isFinite(Number(valorHora)) || Number(valorHora) <= 0) return setError('Informe um valor por hora maior que zero.');
    if (!Number.isInteger(Number(horasSemanais)) || Number(horasSemanais) <= 0 || Number(horasSemanais) > 168) return setError('Informe as horas semanais entre 1 e 168.');

    setLoading(true);
    try {
      const now = new Date().toISOString();

      // Buscar foto de perfil e banner do estabelecimento
      let fotoUsuario = '';
      let bannerUsuario = '';
      try {
        const { list: profiles } = await lumi.entities.user_profiles.list({
          filter: { userId: user.userId },
          limit: 2
        });
        if (profiles?.length !== 1 || profiles[0].profileType !== 'restaurante') { setError('Apenas estabelecimentos podem publicar vagas.'); return; }
        if (profiles && profiles.length > 0) {
          const profile = profiles[0] as { profileImageUrl?: string; bannerUrl?: string };
          fotoUsuario = profile.profileImageUrl || '';
          bannerUsuario = profile.bannerUrl || '';
        }
      } catch (profileErr) {
        setError('Não foi possível confirmar seu perfil. Tente novamente.'); return;
      }

      await lumi.entities.vagas.create({
        userId: user.userId,
        estabelecimentoNome: user.userName || 'Restaurante',
        estabelecimentoEmail: user.email || '',
        titulo: titulo.trim(),
        cargo: cargo,
        tipoContrato: tipoContrato,
        regimeTrabalho: regimeTrabalho,
        valorHora: parseFloat(valorHora),
        horasSemanais: parseInt(horasSemanais) || 0,
        localizacao: localizacao.trim(),
        descricao: descricao.trim(),
        infoContrato: infoContrato.trim(),
        ativa: true,
        fotoUsuario,
        bannerUsuario,
        creator: user.userId,
        createdAt: now,
        updatedAt: now
      });

      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        navigate('/perfil');
      }, 2000);
    } catch (error) {
      setError('Não foi possível publicar a vaga. Seus campos foram mantidos.');
      console.error('Erro ao criar vaga:', error);
    } finally {
      setLoading(false);
    }
  };

  const inputCls = 'w-full bg-[#1a1a1a] border border-[#3a3a3a] rounded-xl px-4 py-3.5 text-gray-300 placeholder-gray-600 focus:border-[#E53935] focus:ring-1 focus:ring-[#E53935] outline-none transition-all text-sm';
  const iconCls = 'absolute left-4 top-1/2 -translate-y-1/2 text-gray-500';
  const labelCls = 'block text-gray-300 text-sm font-medium mb-2';

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-[#1a1a1a] flex flex-col">
      <Navbar />

      <div className="flex-1 flex items-start justify-center px-4 py-12 pt-28">
        <div className="w-full max-w-2xl bg-[#2a2a2a] rounded-2xl border border-[#3a3a3a] shadow-2xl p-6 sm:p-9">
          
          <Link to="/perfil" className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 text-sm">
            <ArrowLeft size={16} /> Voltar ao Perfil
          </Link>

          <div className="mb-8">
            <h1 className="text-2xl font-bold text-white flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#E53935]/20 to-[#C62828]/20 flex items-center justify-center">
                <Briefcase size={20} className="text-[#E53935]" />
              </div>
              Criar Nova Vaga
            </h1>
            <p className="text-gray-500 text-sm ml-12">Preencha os detalhes para publicar uma vaga no seu estabelecimento.</p>
          </div>

          {error && <p role="alert" className="text-red-300 mb-4">{error}</p>}
          <form onSubmit={handleSubmit} className="space-y-6 pl-12">
            
            {/* Titulo */}
            <div>
              <label className={labelCls}>Titulo da Vaga</label>
              <input
                type="text"
                required
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ex: Precisa-se de Barman experiente"
                className={inputCls}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Cargo */}
              <div>
                <label className={labelCls}>Cargo</label>
                <select
                  required
                  value={cargo}
                  onChange={(e) => setCargo(e.target.value)}
                  className={inputCls}
                >
                  {CARGOS.map(c => (
                    <option key={c} value={c}>
                      {c.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </option>
                  ))}
                </select>
              </div>

              {/* Localizacao */}
              <div>
                <label className={labelCls}>Localizacao</label>
                <div className="relative">
                  <MapPin size={18} className={iconCls} />
                  <input
                    type="text"
                    required
                    value={localizacao}
                    onChange={(e) => setLocalizacao(e.target.value)}
                    placeholder="Ex: Navigli, Milao"
                    className={inputCls + ' pl-11'}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Tipo de Contrato */}
              <div>
                <label className={labelCls}>Tipo de Contrato</label>
                <select
                  value={tipoContrato}
                  onChange={(e) => setTipoContrato(e.target.value)}
                  className={inputCls}
                >
                  <option value="fixa">Fixo (Contrato estavel)</option>
                  <option value="temporaria">Temporario</option>
                  <option value="diaria">Diaria / Freelance</option>
                </select>
              </div>

              {/* Regime */}
              <div>
                <label className={labelCls}>Regime de Horas</label>
                <select
                  value={regimeTrabalho}
                  onChange={(e) => setRegimeTrabalho(e.target.value)}
                  className={inputCls}
                >
                  <option value="full_time">Full-time (Inteiro)</option>
                  <option value="part_time">Part-time (Meio periodo)</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {/* Valor Hora */}
              <div>
                <label className={labelCls}>Valor pago por hora (€)</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#E53935] font-bold">€</span>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.5"
                    value={valorHora}
                    onChange={(e) => setValorHora(e.target.value)}
                    placeholder="Ex: 8.50"
                    className={inputCls + ' pl-9 font-bold'}
                  />
                </div>
              </div>

              {/* Horas Semanais */}
              <div>
                <label className={labelCls}>Horas semanais</label>
                <div className="relative">
                  <Clock size={16} className={iconCls} />
                  <input
                    type="number"
                    min="1"
                    max="60"
                    value={horasSemanais}
                    onChange={(e) => setHorasSemanais(e.target.value)}
                    className={inputCls + ' pl-11 bg-[#1a1a1a]'}
                  />
                </div>
              </div>
            </div>

            {/* Info Contrato (Se fixa) */}
            {(
              <div>
                <label className={labelCls}>Detalhes do contrato</label>
                <div className="relative">
                  <FileText size={18} className={iconCls} />
                  <input
                    type="text"
                    value={infoContrato}
                    onChange={(e) => setInfoContrato(e.target.value)}
                    placeholder="Ex.: CCNL aplicável, nível, duração, turnos e período de prova conforme contrato"
                    className={inputCls + ' pl-11'}
                  />
                </div>
              </div>
            )}

            {/* Descricao */}
            <div>
              <label className={labelCls}>Descricao da Vaga</label>
              <textarea
                required
                rows={4}
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Descreva os requisitos, responsabilidades e os beneficios oferecidos..."
                className={inputCls + ' resize-none leading-relaxed'}
              />
            </div>

            <div className="pt-4 flex items-center justify-between border-t border-[#3a3a3a]">
              {saved ? (
                <div className="flex items-center gap-2 text-green-400 font-medium">
                  <CheckCircle2 size={18} />
                  Vaga publicada com sucesso!
                </div>
              ) : <div/>}

              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 bg-gradient-to-r from-[#C62828] to-[#E53935] text-white px-8 py-3.5 rounded-xl font-bold hover:shadow-xl hover:shadow-[#E53935]/30 transition-all border border-[#8D0000] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Publicando...' : 'Publicar Vaga'}
              </button>
            </div>

          </form>

        </div>
      </div>

      <Footer />
    </div>
  );
}

export default CriarVaga;